// Section 03 — The 18-agent dual-swarm engine.
//
// Replaces the legacy 5-agent grid. Doctrine source:
//   STRIKZA_DualSwarm_Architecture v1.0.0 + ACC v1.0
//
// Display strategy:
//   - One section per swarm (SWARM 1 — Claude, SWARM 2 — OpenAI,
//     SHARED OPS) with provider + model labels.
//   - Each swarm shows its purpose and strength up front so the
//     visitor sees WHY two providers, not just "lots of agents".
//   - Routing call-out at the bottom shows when each swarm fires
//     (daily ≤4 legs · 5+ legs · POWERBALL · DUAL_AUDIT > GH₵10k).
//   - Anti-tipster framing kept verbatim.

import { Fragment } from "react";
import { AgentCard } from "./AgentCard.tsx";
import { SWARMS, HIGH_STAKES_FLOAT_THRESHOLD_GHS } from "./landing-data.ts";

const FLOW_STEPS: ReadonlyArray<{ step: string; label: string; sub: string }> = [
  {
    step: "01",
    label: "Agents debate",
    sub: "Two swarms · sequential cache, parallel within stage",
  },
  { step: "02", label: "You approve", sub: "One tap, every agent's reasoning visible" },
  { step: "03", label: "Chain compounds", sub: "Stake rolls forward · half off the table" },
];

const ROUTING_RULES: ReadonlyArray<{ trigger: string; swarm: string }> = [
  { trigger: "Daily slip · ≤ 4 legs", swarm: "SWARM 1 — Claude" },
  { trigger: "Heavy accumulator · 5+ legs", swarm: "SWARM 2 — OpenAI" },
  { trigger: "POWERBALL 10 · MEGA POWERBALL 20", swarm: "SWARM 2 — OpenAI" },
  { trigger: "Season-long markets (title · relegation · top scorer)", swarm: "SWARM 2 — OpenAI" },
  {
    trigger: `User float > GH₵${HIGH_STAKES_FLOAT_THRESHOLD_GHS.toLocaleString()} · daily slip`,
    swarm: "DUAL AUDIT — both swarms run, conflicts surfaced",
  },
];

export function AgentSwarm(): JSX.Element {
  return (
    <section
      id="agents"
      className="sz-l-section"
      style={{
        position: "relative",
        padding: "140px 0 160px",
        borderTop: "1px solid var(--border)",
      }}
    >
      <div className="kente-bg" />
      <div className="container" style={{ position: "relative" }}>
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: 80,
            marginBottom: 80,
          }}
        >
          <div>
            <span className="bracket">Section 03 · Engine</span>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              Eighteen agents.{" "}
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>Two swarms.</em>
              <br />
              One slip you sign.
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 620,
                lineHeight: 1.55,
              }}
            >
              STRIKZA isn&apos;t a tipster. It&apos;s two specialised AI swarms — Claude for
              multi-turn debate on daily slips, OpenAI for combinatorial assembly on POWERBALL and
              heavy accumulators — plus four shared platform agents. They argue with each other
              until consensus emerges. You see the debate. You sign the slip.
            </p>
          </div>
        </div>

        {/* Swarms */}
        {SWARMS.map((swarm) => {
          const cols = swarm.id === "shared_ops" ? 4 : Math.min(swarm.agents.length, 4);
          return (
            <div
              key={swarm.id}
              style={{
                marginTop: 56,
                paddingTop: 32,
                borderTop: "1px solid var(--border)",
              }}
            >
              {/* Swarm header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "end",
                  gap: 24,
                  marginBottom: 28,
                }}
              >
                <div>
                  <div
                    className="display"
                    style={{
                      fontSize: 28,
                      color: "var(--text)",
                      letterSpacing: "0.005em",
                      marginBottom: 8,
                    }}
                  >
                    {swarm.name}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.16em",
                      color: "var(--accent)",
                      textTransform: "uppercase",
                      marginBottom: 14,
                    }}
                  >
                    {swarm.subtitle}
                  </div>
                  <p
                    style={{
                      fontSize: 14,
                      color: "var(--text-muted)",
                      lineHeight: 1.55,
                      maxWidth: 720,
                    }}
                  >
                    <span style={{ color: "var(--text)" }}>{swarm.purpose}</span> {swarm.strength}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.18em",
                      color: "var(--text-dim)",
                      textTransform: "uppercase",
                    }}
                  >
                    Provider
                  </div>
                  <div
                    className="display"
                    style={{
                      fontSize: 18,
                      color: "var(--accent)",
                      letterSpacing: "0.01em",
                      marginTop: 4,
                    }}
                  >
                    {swarm.provider}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.1em",
                      color: "var(--text-muted)",
                      marginTop: 6,
                    }}
                  >
                    {swarm.model}
                  </div>
                </div>
              </div>

              {/* Agent grid */}
              <div
                className={`sz-l-agent-grid sz-l-agent-grid-${swarm.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${String(cols)}, 1fr)`,
                  gap: 16,
                }}
              >
                {swarm.agents.map((a, i) => (
                  <AgentCard key={a.id} agent={a} idx={i} />
                ))}
              </div>
            </div>
          );
        })}

        {/* Routing logic */}
        <div
          style={{
            marginTop: 64,
            padding: "32px 36px",
            border: "1px solid var(--border)",
            background: "rgba(17, 17, 17, 0.6)",
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--accent)",
              textTransform: "uppercase",
              marginBottom: 18,
            }}
          >
            Which swarm fires when
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {ROUTING_RULES.map((r, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr",
                  gap: 18,
                  paddingBlock: 10,
                  borderBottom:
                    i < ROUTING_RULES.length - 2 ? "1px solid var(--border)" : undefined,
                  alignItems: "baseline",
                }}
              >
                <div style={{ fontSize: 14, color: "var(--text)" }}>{r.trigger}</div>
                <div
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: "var(--accent-dim)",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  {r.swarm}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User flow steps */}
        <div
          className="sz-l-flow-strip"
          style={{
            marginTop: 56,
            padding: "32px 36px",
            border: "1px solid var(--border)",
            background: "rgba(17, 17, 17, 0.6)",
            display: "grid",
            gridTemplateColumns: "auto 1fr auto 1fr auto 1fr",
            alignItems: "center",
            gap: 20,
          }}
        >
          {FLOW_STEPS.map((s, i) => (
            <Fragment key={i}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <span
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: "var(--accent)",
                    letterSpacing: "0.18em",
                  }}
                >
                  {s.step}
                </span>
              </div>
              <div>
                <div
                  className="serif"
                  style={{ fontSize: 22, color: "var(--text)", fontStyle: "italic" }}
                >
                  {s.label}
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: "var(--text-muted)",
                    letterSpacing: "0.1em",
                    marginTop: 4,
                    textTransform: "uppercase",
                  }}
                >
                  {s.sub}
                </div>
              </div>
              {i < 2 && (
                <div
                  className="sz-l-flow-divider"
                  style={{
                    width: 40,
                    height: 1,
                    background:
                      "linear-gradient(90deg, transparent, var(--accent-dim), transparent)",
                    marginRight: -10,
                  }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
